import React from "react";
import { Button } from "../components/ui/button";
import { Printer } from "lucide-react";
import { company, doddServices } from "../mock";
import { LogoMark } from "../components/Logo";
import useSeo from "../hooks/useSeo";

const ProviderSheet = () => {
  useSeo({
    title: "Provider Information Sheet | Caregivers Alliance Homecare",
    description: "One-page provider information sheet for Caregivers Alliance Homecare LLC — DODD certified services, contact details and referral link.",
    path: "/provider-sheet",
    robots: "noindex,nofollow",
  });

  return (
    <div className="bg-slate-100 min-h-screen py-8 print:bg-white print:py-0">
      <div className="max-w-[8.5in] mx-auto mb-4 flex justify-end px-4 print:hidden">
        <Button data-testid="provider-sheet-print-btn" onClick={() => window.print()} className="rounded-full bg-[#0E5A72] hover:bg-[#0A4557] text-white">
          <Printer className="mr-2 h-4 w-4" /> Print / Save as PDF
        </Button>
      </div>

      <div data-testid="provider-sheet" className="bg-white max-w-[8.5in] mx-auto shadow-lg print:shadow-none p-10">
        <div className="flex items-center justify-between border-b-4 border-[#0E5A72] pb-5">
          <div className="flex items-center gap-3">
            <LogoMark className="h-14 w-14 rounded-2xl" />
            <div>
              <div className="font-display font-extrabold text-2xl text-[#0E2A33]">Caregivers Alliance Homecare LLC</div>
              <div className="text-sm text-[#2E9E6A] font-semibold tracking-wide">OHIO HOME CARE & DODD PROVIDER</div>
            </div>
          </div>
          <img
            src="https://api.qrserver.com/v1/create-qr-code/?size=110x110&data=https%3A%2F%2Fmyohcare.com%2Frefer"
            alt="QR code to myohcare.com/refer"
            className="h-[100px] w-[100px]"
          />
        </div>

        <div className="grid grid-cols-2 gap-8 mt-6">
          <div>
            <h2 className="font-display font-bold text-[#0E5A72] text-lg mb-3">Agency Information</h2>
            <table className="text-sm text-slate-700 w-full">
              <tbody>
                <tr><td className="font-semibold pr-3 py-1 align-top">Address</td><td>{company.address}<br />{company.city}</td></tr>
                <tr><td className="font-semibold pr-3 py-1">Phone</td><td>{company.phone}</td></tr>
                <tr><td className="font-semibold pr-3 py-1">Fax</td><td>{company.fax}</td></tr>
                <tr><td className="font-semibold pr-3 py-1">Email</td><td>{company.email}</td></tr>
                <tr><td className="font-semibold pr-3 py-1">Website</td><td>https://myohcare.com/</td></tr>
              </tbody>
            </table>
          </div>
          <div>
            <h2 className="font-display font-bold text-[#0E5A72] text-lg mb-3">
              DODD Certified Services
            </h2>
            <div className="text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-3">
              DODD Certified Provider — Now Accepting New Patients
            </div>
            <ul className="text-sm text-slate-700 space-y-1.5">
              {doddServices.map((s) => <li key={s}>✓ {s}</li>)}
            </ul>
          </div>
        </div>

        <div className="mt-6 bg-[#F7FBFC] border border-slate-200 rounded-xl p-5">
          <h3 className="font-display font-bold text-[#0E2A33] mb-2">Additional Pathways</h3>
          <p className="text-sm text-slate-600 leading-relaxed">
            Personal care, homemaker support, companionship, respite and transportation support are available on a
            private-pay basis where staffing allows. Family-caregiver options may be available under Ohio
            participant-directed programs — eligibility depends on the waiver, service plan and program rules.
            Contact us to verify plan eligibility, authorization and current participation before services begin.
          </p>
        </div>

        <div className="mt-6 text-center border-t border-slate-200 pt-4">
          <div className="font-display font-bold text-[#0E5A72]">Send a referral: {company.phone} · {company.email}</div>
          <div className="text-xs text-slate-400 mt-1">
            Headquartered in Columbus, Ohio · Statewide DODD referral coverage · Actual service availability depends on
            staffing, location, schedule and individual support requirements.
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProviderSheet;
